/**
 * Servicio para manejar operaciones relacionadas con proyectos
 */

import axios from 'axios';

/**
 * Obtiene la lista de proyectos de la empresa
 * @returns {Promise<Array>} - Lista de proyectos
 */
export const getProyectosEmpresa = async () => {
  try {
    console.log(`🔍 [proyectoService] Obteniendo proyectos de empresa...`);

    // Usar el servicio de preguntas frecuentes para obtener los proyectos
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      throw new Error('No hay token de autenticación');
    }

    const response = await axios.get(`/api/proyecto/empresa`, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    // La API ahora retorna directamente el array
    if (Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  } catch (error) {
    console.error(`❌ [proyectoService] Error obteniendo proyectos:`, error);
    throw error;
  }
};

/**
 * Obtiene la información de un proyecto específico
 * @param {string|number} codigoProyecto - Código del proyecto
 * @returns {Promise<Object>} - Información del proyecto
 */
export const getProyectoInfo = async (codigoProyecto) => {
  try {
    console.log(`🔍 [proyectoService] Obteniendo información del proyecto ${codigoProyecto}...`);

    const authToken = localStorage.getItem('token');
    const headers = { Accept: 'application/json' };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await axios.get(`/api/proyecto/info/${codigoProyecto}`, {
      timeout: 10000,
      headers
    });

    if (response.data.Status === 'Success' && response.data.data) {
      const proyectoData = response.data.data;

      console.log(`✅ [proyectoService] Información del proyecto obtenida:`, proyectoData);
      return {
        Status: 'Success',
        data: proyectoData
      };
    } else {
      throw new Error('No se encontró información del proyecto');
    }
  } catch (error) {
    console.error(`❌ [proyectoService] Error obteniendo información del proyecto:`, error);

    // Si el endpoint no existe, retornar estructura vacía
    if (error.response && error.response.status === 404) {
      console.log(`⚠️ [proyectoService] Endpoint de proyecto no encontrado, retornando estructura vacía`);
      // Inicializar horario como array con un elemento por defecto
      const horarioDefault = JSON.stringify([{
        num_dia_semana_inicio: 1,
        num_dia_semana_fin: 5,
        hora_inicio: '09:00:00',
        hora_fin: '18:00:00'
      }]);

      return {
        Status: 'Success',
        data: {
          codigo_proyecto: codigoProyecto,
          nombre_proyecto: '',
          acerca_proyecto: '',
          fecha_estimada_entrega: '',
          tipos_unidades: '',
          exhibicion_unidades: '',
          etapa_actual: '',
          direccion_proyecto: '',
          direccion_sala_ventas: '',
          horario_atencion: horarioDefault,
          recorrido_virtual: '',
          unidades_disponibles: ''
        }
      };
    }

    throw error;
  }
};

/**
 * Actualiza la información de un proyecto
 * @param {string|number} codigoProyecto - Código del proyecto
 * @param {Object} proyectoData - Datos del proyecto a actualizar
 * @returns {Promise<Object>} - Respuesta de la actualización
 */
export const updateProyectoInfo = async (codigoProyecto, proyectoData) => {
  try {
    console.log(`💾 [proyectoService] Actualizando información del proyecto ${codigoProyecto}...`, proyectoData);

    // Procesar horario_atencion: convertir de JSON string a array si es necesario
    let horarioAtencion = null;
    if (proyectoData.horario_atencion) {
      // Función para normalizar formato de hora (HH:MM -> HH:MM:SS)
      const normalizarHora = (hora) => {
        if (!hora) return null;
        // Si ya tiene formato HH:MM:SS, retornarlo
        if (hora.length >= 8) return hora;
        // Si tiene formato HH:MM, agregar :00
        if (hora.length === 5) return hora + ':00';
        return hora;
      };

      // Función para normalizar un objeto de horario
      const normalizarHorario = (h) => {
        return {
          num_dia_semana_inicio: h.num_dia_semana_inicio ?? h.dia_inicio ?? 1,
          num_dia_semana_fin: h.num_dia_semana_fin ?? h.dia_fin ?? 5,
          hora_inicio: normalizarHora(h.hora_inicio) || '09:00:00',
          hora_fin: normalizarHora(h.hora_fin) || '18:00:00'
        };
      };

      if (typeof proyectoData.horario_atencion === 'string') {
        try {
          const parsed = JSON.parse(proyectoData.horario_atencion);
          // Asegurar que siempre sea un array y normalizar cada elemento
          const horariosArray = Array.isArray(parsed) ? parsed : [parsed];
          horarioAtencion = horariosArray.map(normalizarHorario);
          console.log(`📋 [proyectoService] Horario parseado (string -> array normalizado):`, horarioAtencion);
        } catch (e) {
          console.error(`❌ [proyectoService] Error parseando horario_atencion:`, e);
          // Si no es JSON válido, usar null
          horarioAtencion = null;
        }
      } else {
        // Si ya es un objeto o array, asegurar que sea array y normalizar
        const horariosArray = Array.isArray(proyectoData.horario_atencion)
          ? proyectoData.horario_atencion
          : [proyectoData.horario_atencion];
        horarioAtencion = horariosArray.map(normalizarHorario);
        console.log(`📋 [proyectoService] Horario procesado (objeto/array -> array normalizado):`, horarioAtencion);
      }
      // Assign the processed horarioAtencion back to proyectoData
      proyectoData.horario_atencion = horarioAtencion;
    } else {
      console.log(`⚠️ [proyectoService] No hay horario_atencion en proyectoData`);
    }

    // Enviar datos tal cual, el backend maneja el mapeo o usa las mismas claves
    console.log(`📤 [proyectoService] Datos a enviar al backend:`, JSON.stringify(proyectoData, null, 2));

    const authToken = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await axios.patch(`/api/proyecto/update/${codigoProyecto}`, proyectoData, {
      timeout: 15000,
      headers
    });

    if (response.data.Status === 'Success') {
      console.log(`[proyectoService] Información del proyecto actualizada exitosamente`);
      return {
        Status: 'Success',
        message: response.data.message || 'Información del proyecto actualizada exitosamente'
      };
    } else {
      throw new Error(response.data.Error || response.data.message || 'Error desconocido al actualizar información del proyecto');
    }
  } catch (error) {

    // Manejar diferentes tipos de errores
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          throw new Error('Datos inválidos proporcionados.');
        case 401:
          throw new Error('No autorizado. Por favor, inicie sesión nuevamente.');
        case 404:
          throw new Error(data?.Error || data?.message || 'Ruta no encontrada. Verifique que el endpoint esté disponible.');
        case 500:
          throw new Error('Error interno del servidor.');
        default:
          throw new Error(data?.Error || data?.message || 'Error desconocido al actualizar información del proyecto');
      }
    }

    if (error.code === 'ECONNABORTED') {
      throw new Error('Tiempo de espera agotado. Verifique su conexión.');
    }

    throw error;
  }
};

export default {
  getProyectosEmpresa,
  getProyectoInfo,
  updateProyectoInfo
};

